import { useCallback, useContext, useEffect, useState } from "react"
import _ from "lodash"

// Context
import { UserContext } from "../../../context/userContext"

//Formik
import { Form, Formik } from "formik"
import * as Yup from "yup"

//Prime react
import { Button } from "primereact/button"
import { ConfirmDialog } from "primereact/confirmdialog" // For <ConfirmDialog /> component
import { confirmDialog } from "primereact/confirmdialog" // For confirmDialog method
import { Badge } from "primereact/badge"
import { Tooltip } from "primereact/tooltip"
import { Dialog } from "primereact/dialog"
import { TabView, TabPanel } from "primereact/tabview"

//Styles
import myStyles from "../../../styles/assessments/ManagementForm.module.css"

//Assessments components
import {
  assessmentsApiCreatePost,
  assessmentsApiGet,
  assessmentsApiUpdatePut,
} from "../assessmentsApi"
import ManagementFormProfile from "./ManagementFormProfile"
import ManagementFormOutcomes from "./ManagementFormOutcomes"

//Helpers
import { LogError } from "../../../helpers/logger"
import { LoaderMedium } from "../../../components/Loaders"
import FormikErrorNotification from "../../../helpers/formikInputs/FormikErrorNotification"
import ManagementFormBuilder from "./ManagementFormBuilder"
import elementTypes from "./FormBuilder/Elements/elementTypes"
import { isAppManager } from "../../../helpers/staffRoleHelpers"

export default function ManagementForm({
  assessmentProfileId,
  activeOrganizationId,
  activeOrganizationAlias,
  organizations,
  onSaved,
  onDelete,
  onCancel,
}) {
  const userCtx = useContext(UserContext)

  const [isLoading, setIsLoading] = useState(false)
  const [questionsUpdated, setQuestionUpdated] = useState(false)
  const [readOnly, setReadOnly] = useState(false)
  const [profile, setProfile] = useState({
    userId: userCtx.session.user.staff_id,
    userAlias: userCtx.session.user.staff_alias,
    enterpriseId: userCtx.session.user.enterprise_id,
    enterpriseAlias: userCtx.session.user.enterprise_alias,
    organizationId: activeOrganizationId,
    organizationAlias: activeOrganizationAlias,
    assessment: {
      id: undefined,
      name: "",
      description: "",
      title: "Assessment title",
      titleSize: 0, //0 == medium
      scoreCalculation: "",
      displayScore: true,
      displayDisposition: true,
      displayRemarks: true,
      displayQuestions: true,
      displayAnswers: true,
      displayAnswerValues: true,
      accessLevel: "private",
      enabled: true,
      validateScore: false,
      minScore: 0,
      maxScore: 0,
      isSystemType: false,
      systemTypeCode: "",
    },
    questions: [],
    suggestedOutcomes: [],
    organizations: [],
  })

  Yup.addMethod(Yup.array, "noOutcomeScoreRange", function () {
    return this.test("noRange", "", function (list) {
      const errors = []

      list.forEach((item, index) => {
        //Check if min score = max score
        if (!item.isDeleted) {
          if (item.scoreMin === item.scoreMax) {
            errors.push(
              this.createError({
                path: `${this.path}[${index}].scoreMin`,
                message: "There is no range between min and max score",
              }),
            )

            errors.push(
              this.createError({
                path: `${this.path}[${index}].scoreMax`,
                message: "There is no range between min and max score",
              }),
            )
          } else if (item.scoreMin > item.scoreMax) {
            errors.push(
              this.createError({
                path: `${this.path}[${index}].scoreMax`,
                message: "The max score cannot be less than the min",
              }),
            )
          }
        }
      })

      if (!_.isEmpty(errors)) {
        throw new Yup.ValidationError(errors)
      }

      return true
    })
  })

  Yup.addMethod(Yup.object, "validateQuestionOrder", function () {
    return this.test("validateQuestionOrder", "", function (questionOrder) {
      let errors = []

      try {
        if (!questionOrder.isDeleted) {
          //Validate question
          if (
            questionOrder.question !== undefined &&
            questionOrder.question !== null
          ) {
            if (!questionOrder.question.text) {
              errors.push(
                this.createError({
                  path: `${this.path}.question.text`,
                  message: "Please specify the label value",
                }),
              )
            }
            if (questionOrder.question.options?.length > 0) {
              questionOrder.question.options.forEach((option, index) => {
                if (!option.isDeleted) {
                  if (
                    option.scoreValue === null ||
                    option.scoreValue === undefined
                  ) {
                    errors.push(
                      this.createError({
                        path: `${this.path}.question.options[${index}].scoreValue`,
                        message: "Please specify the score value",
                      }),
                    )
                  }

                  if (!option.text) {
                    errors.push(
                      this.createError({
                        path: `${this.path}.question.options[${index}].text`,
                        message: "Please specify the label value",
                      }),
                    )
                  }
                }
              })
            }
          }

          if (
            questionOrder.questionGroup !== undefined &&
            questionOrder.questionGroup !== null
          ) {
            if (!questionOrder.questionGroup.name) {
              errors.push(
                this.createError({
                  path: `${this.path}.questionGroup.name`,
                  message: "Please specify the section name value",
                }),
              )
            }

            questionOrder.questionGroup.questions?.forEach(
              (question, index) => {
                //Check if min score = max score
                if (question.isActive) {
                  if (!question.text) {
                    errors.push(
                      this.createError({
                        path: `${this.path}.questionGroup.questions[${index}].text`,
                        message: "Please specify the label value",
                      }),
                    )
                  }
                }
                //Validate question options
                if (question.options?.length > 0) {
                  question.options.forEach((option, index) => {
                    if (!option.isDeleted) {
                      if (
                        option.scoreValue === null ||
                        option.scoreValue === undefined
                      ) {
                        errors.push(
                          this.createError({
                            path: `${this.path}.questionGroup.questions[${index}].options[${index}].scoreValue`,
                            message: "Please specify the score value",
                          }),
                        )
                      }

                      if (!option.text) {
                        errors.push(
                          this.createError({
                            path: `${this.path}.questionGroup.questions[${index}].options[${index}].text`,
                            message: "Please specify the label value",
                          }),
                        )
                      }
                    }
                  })
                }
              },
            )
          }
        }
      } catch (error) {
        LogError("Unable to validate question order entry", {
          error,
          questionOrder,
        })

        errors.push("Unable to validate values")
      }

      if (!_.isEmpty(errors)) {
        throw new Yup.ValidationError(errors)
      }

      return true
    })
  })

  const validationSchema = Yup.object().shape({
    assessment: Yup.object().shape({
      name: Yup.string().required("Please specify the assessment name"),
      validateScore: Yup.bool(),
      minScore: Yup.number().when("validateScore", {
        is: true,
        then: Yup.number().required("Please specify the minimum score"),
      }),
      maxScore: Yup.number().when(
        ["validateScore", "minScore"],
        (validateScore, minScore, schema) => {
          return validateScore
            ? Yup.number()
                .required("Please specify the maximum score")
                .moreThan(
                  minScore,
                  "The maximum score must be more than the minimum",
                )
            : schema
        },
      ),
      isSystemType: Yup.bool(),
      systemTypeCode: Yup.string()
        .nullable()
        .when("isSystemType", (isSystemType, schema) => {
          return isSystemType
            ? Yup.string()
                .nullable()
                .required("Please specify an assessment code")
            : schema
        }),
    }),
    suggestedOutcomes: Yup.array().of(
      Yup.object().shape({
        isDeleted: Yup.bool(),
        scoreMin: Yup.number().when("isDeleted", {
          is: false,
          then: Yup.number().required("Please enter a minimum score"),
        }),
        scoreMax: Yup.number().when("isDeleted", {
          is: false,
          then: Yup.number()
            .required("Please enter a maximum score")
            .min(
              Yup.ref("scoreMin"),
              "The maximum score cannot be less than the minimum score",
            ),
        }),
        text: Yup.string().when("isDeleted", {
          is: false,
          then: Yup.string().required("Please enter text for the outcome"),
        }),
      }),
    ),
    questions: Yup.array().of(Yup.object().validateQuestionOrder()),
  })

  const loadProfile = useCallback(async () => {
    if (assessmentProfileId) {
      try {
        setIsLoading(true)
        const profile = await assessmentsApiGet(
          userCtx.tokens.getAssessmentsToken(),
          assessmentProfileId,
        )

        if (profile) {
          profile.organizationIds = profile.organizations
            .filter(
              (organization) =>
                organization.organizationId !==
                  profile.assessment.organizationId && organization.isActive,
            )
            .map((organization) => organization.organizationId)

          if (
            profile.assessment.isSystemType &&
            userCtx.session.user.staff_role === 1
          ) {
            setReadOnly(false)
          } else {
            setReadOnly(
              profile.assessment.organizationId !== activeOrganizationId ||
                profile.assessment.isSystemType,
            )
          }
        }

        setProfile(profile)
        setIsLoading(false)
      } catch (error) {
        LogError(
          `Unable to load assessment profile for ID ${assessmentProfileId}`,
          error,
        )
        userCtx.setNotificationError("Unable to load assessment profile")
        onCancel()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentProfileId])

  useEffect(() => {
    loadProfile()
  }, [loadProfile])

  const cleanQuestionProfile = (question) => {
    //Fix question labels
    if (elementTypes.isMatrixType(question.type)) {
      question.questionLabels = question.questionLabels.map((questionLabel) => {
        if (questionLabel.isNew) {
          return { ...questionLabel }
        } else {
          return questionLabel
        }
      })
    }

    return question
  }

  //Remove new entries that have been deleted
  const cleanupQuestions = (questions) => {
    //Remove questionOrders that are new and have been removed
    let cleanQuestion = questions.filter(
      (questionOrder) => !questionOrder.isNew || !questionOrder.isDeleted,
    )

    //Remove questionOrders with questions/questionGroups that are new and indicate that they are removed
    cleanQuestion = cleanQuestion.filter((questionOrder) => {
      if (
        questionOrder.question &&
        questionOrder.question.isNew &&
        !questionOrder.question.isActive
      ) {
        return false
      } else if (
        questionOrder.questionGroup &&
        questionOrder.questionGroup.isNew &&
        questionOrder.questionGroup.isDeleted
      ) {
        return false
      } else {
        return true
      }
    })

    //Ensure that all questionGroup questions are cleaned up
    cleanQuestion = cleanQuestion.map((questionOrder) => {
      if (questionOrder.questionGroup) {
        //Filter out new questions that are not active
        const cleanQuestions = questionOrder.questionGroup.questions.filter(
          (question) => !question.isNew || question.isActive,
        )

        if (
          cleanQuestions.length === questionOrder.questionGroup.questions.length
        ) {
          return questionOrder
        }

        // Return a new object to avoid mutation
        return {
          ...questionOrder,
          questionGroup: {
            ...questionOrder.questionGroup,
            questions: cleanQuestions,
          },
        }
      }

      return questionOrder
    })

    //Force all new questionOrders to have null ids (We needed temp ids for drag & drop)
    cleanQuestion = cleanQuestion.map((questionOrder) => {
      let questionOrderId = questionOrder.isNew ? undefined : questionOrder.id

      //Set the questionGroup ID to NULL if it is new
      let questionGroup = undefined

      if (questionOrder.questionGroup) {
        questionGroup = { ...questionOrder.questionGroup }
        questionGroup.tempId = questionGroup.id

        questionGroup.questions = questionGroup.questions.map((question) => {
          return cleanQuestionProfile(question)
        })
      } else if (questionOrder.question) {
        //When a question is moved into a questionGroup, the questionOrder will have removeQuestion set to 1
        //If we don't remove the question here, the old question profile might override changes to the question while saving
        if (questionOrder.removeQuestion) {
          questionOrder.question = undefined
        } else {
          questionOrder.question = cleanQuestionProfile(questionOrder.question)
        }
      }

      return {
        ...questionOrder,
        id: questionOrderId,
        questionGroup: questionGroup,
      }
    })

    return cleanQuestion
  }

  //Remove new entries that have been deleted
  const cleanupOutcomes = (outcomes) => {
    let cleanOutcomes = outcomes.filter(
      (outcome) => !outcome.isNewOutcome || !outcome.isDeleted,
    )

    return cleanOutcomes
  }

  const updateOrganizations = (
    creatingOrganizationId,
    organizations,
    organizationIds,
  ) => {
    let updatedOrganizations = []

    organizations.forEach((organization) => {
      //If the organizationId is in the list of organizationIds it should remain as is, else it should be set as inactive.
      if (
        creatingOrganizationId === organization.organizationId ||
        organizationIds.includes(organization.organizationId)
      ) {
        updatedOrganizations.push({ ...organization, isActive: true })
      } else {
        updatedOrganizations.push({ ...organization, isActive: false })
      }
    })

    if (organizationIds) {
      const newOrganizations = organizationIds
        ?.filter(
          (organizationId) =>
            !organizations.some(
              (organization) => organization.organizationId === organizationId,
            ),
        )
        .map((organizationId) => {
          return { id: null, organizationId: organizationId, isActive: true }
        })

      return [...updatedOrganizations, ...newOrganizations]
    } else {
      return updatedOrganizations
    }
  }

  const handleFormSubmit = async (saveValues) => {
    try {
      let values = _.cloneDeep(saveValues)

      values.questions = cleanupQuestions(values.questions)
      values.suggestedOutcomes = cleanupOutcomes(values.suggestedOutcomes)
      values.organizations = updateOrganizations(
        values.assessment.organizationId || activeOrganizationId,
        values.organizations,
        values.organizationIds,
      )

      //Only save new outcomes that are not deleted
      values.suggestedOutcomes = values.suggestedOutcomes.filter(
        (outcome) =>
          (outcome.isNewOutcome && !outcome.isDeleted) || !outcome.isNewOutcome,
      )

      if (!values.assessment.id) {
        const result = await assessmentsApiCreatePost(
          userCtx.tokens.getAssessmentsToken(),
          values,
        )

        if (!result.assessment.id) {
          userCtx.setNotificationError("Unable to save new assessment")
          LogError("Unable to save assessment", result)
        } else {
          onSaved()
        }
      } else {
        const updateValues = {
          userId: userCtx.session.user.staff_id,
          organizationID: activeOrganizationId,
          ...values,
        }

        if (updateValues.assessment.isSystemType) {
          if (isAppManager(userCtx.session.user.staff_role)) {
            updateValues.organizationID = updateValues.assessment.organizationId
          } else {
            userCtx.setNotificationError(
              "You do not have permission to edit this assessment",
            )
            return
          }
        }

        const result = await assessmentsApiUpdatePut(
          userCtx.tokens.getAssessmentsToken(),
          updateValues,
        )

        if (!result.assessment.id) {
          userCtx.setNotificationError(
            "Unable to save changes to assessment profile",
          )
          LogError("Unable to save assessment", result)
        } else {
          onSaved()
        }
      }
    } catch (error) {
      if (error.response?.status === 401) {
        LogError("Unable to submit assessment data", error)
        userCtx.setNotificationError(
          `Unauthrorized update request: ${
            error?.response?.data || error?.message
          }`,
        )
      } else {
        LogError("Unable to submit assessment data", error)
        userCtx.setNotificationError("Unable to submit assessment data")
      }
    }
  }

  const RenderTabPanelHeader = (header, errors) => {
    const tooltipTarget = `.tt-Tab${header}`
    return (
      <div>
        <Tooltip target={tooltipTarget} />
        {header}{" "}
        {errors ? (
          <span
            className={tooltipTarget}
            data-pr-tooltip={"Field errors found"}
          >
            <Badge severity="danger" style={{ fontSize: 12 }} />
          </span>
        ) : null}
      </div>
    )
  }

  const RenderAssessmentFormHeader = (isNew, isSystemType) => {
    let headerText = `${isNew ? "New " : ""}Assessment`

    if (isSystemType) {
      headerText = `[System] ${headerText}`
    }

    return (
      <div className={myStyles.dialogHeader}>
        <div className={myStyles.dialogHeaderText}>{headerText}</div>
        <div className={myStyles.dialogHeaderCancel}></div>
        <div className={myStyles.dialogHeaderSave}></div>
      </div>
    )
  }

  return (
    <Dialog
      header={RenderAssessmentFormHeader(
        !profile.assessment.id,
        profile.assessment.isSystemType,
      )}
      visible={true}
      onHide={onCancel}
      className={myStyles.dialog}
      closable={false}
      resizable={true}
    >
      {isLoading ? (
        <LoaderMedium />
      ) : (
        <>
          <ConfirmDialog />
          <Formik
            initialValues={profile}
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={async (values) => {
              await handleFormSubmit(values)
            }}
          >
            {({
              handleSubmit,
              isSubmitting,
              dirty,
              values,
              initialValues,
              setFieldValue,
              getFieldMeta,
              errors,
              isValid,
            }) => (
              <Form autoComplete="off" className={myStyles.form}>
                <FormikErrorNotification />
                <TabView className={`formTabView`}>
                  <TabPanel
                    header={RenderTabPanelHeader(
                      "Configuration",
                      dirty ? errors?.assessment : undefined,
                    )}
                  >
                    <ManagementFormProfile
                      disabled={isSubmitting || readOnly}
                      activeOrganization={activeOrganizationId}
                      organizations={organizations}
                    />
                  </TabPanel>
                  <TabPanel
                    className={myStyles.formBuilderPanel}
                    header={RenderTabPanelHeader(
                      "Form builder",
                      dirty ? errors?.questions : undefined,
                    )}
                  >
                    <ManagementFormBuilder
                      questionOrders={[...values?.questions]}
                      title={values?.assessment?.title}
                      titleSize={values?.assessment?.titleSize}
                      disabled={isLoading || isSubmitting || readOnly}
                      onSetFieldValue={(field, value) => {
                        setFieldValue(field, value)
                        setQuestionUpdated(true)
                      }}
                    />
                    {/*                     <ManagementFormQuestions
                      questionOrders={values?.questions}
                      disabled={isLoading || isSubmitting || readOnly}
                      onSetFieldValue={(field, value) => {
                        setFieldValue(field, value)
                        setQuestionUpdated(true)
                      }}
                    /> */}
                  </TabPanel>
                  <TabPanel
                    header={RenderTabPanelHeader(
                      "Outcomes",
                      dirty ? errors?.suggestedOutcomes : undefined,
                    )}
                  >
                    <ManagementFormOutcomes
                      disabled={isLoading || isSubmitting || readOnly}
                      values={values}
                      initialValues={initialValues}
                      onSetFieldValue={setFieldValue}
                      onGetFieldMeta={getFieldMeta}
                    />
                  </TabPanel>
                </TabView>
                <div className={myStyles.formButtonContainer}>
                  <Button
                    label={"Cancel"}
                    type={"button"}
                    className={myStyles.button}
                    onClick={onCancel}
                  />
                  {!readOnly ? (
                    <>
                      {values.assessment.id ? (
                        <Button
                          label="Delete assessment"
                          type="button"
                          onClick={() => {
                            confirmDialog({
                              message:
                                "Are you sure you want to delete this assessment?",
                              header: "Confirmation",
                              icon: "pi pi-exclamation-triangle",
                              defaultFocus: "accept",
                              accept: () => {
                                onDelete(values.assessment.id)
                              },
                              reject: () => {},
                            })
                          }}
                          className={myStyles.button}
                          disabled={isSubmitting}
                        />
                      ) : null}

                      <Button
                        label={"Save Assessment"}
                        type={"submit"}
                        className={myStyles.button}
                        disabled={(!dirty && !questionsUpdated) || isSubmitting}
                      />
                    </>
                  ) : null}
                </div>
              </Form>
            )}
          </Formik>
        </>
      )}
    </Dialog>
  )
}
